import { useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import classNames from 'classnames'
import * as grid from '@campgladiator/cgui-core.styles.helpers.grid'
import {
  getTrainer,
  trainerSelector,
} from '../../redux/features/OnDemand/Trainer'
import {
  getTrainerSchedule,
  trainerScheduleReset,
  trainerScheduleSelector,
} from '../../redux/features/OnDemand/TrainerSchedule'
import { authSelector, redirectToPath } from '../../redux/features/Auth'
import { useAppDispatch, useAppSelector } from '../../redux/store'
import { breakpoints } from '../../common/utility/breakpoints'
import { useDeviceWidth } from '../../common/hooks'
import { CoverPhotoPanel } from './components/CoverPhotoPanel'
import { TrainerAvatar } from './components/TrainerAvatar'
import { TrainerInfo } from './components/TrainerInfo'
import { ActionPanel } from './components/ActionPanel'
import { AboutMe } from './components/AboutMe'
import { FitnessBackground } from './components/FitnessBackground'
import { LetsConnect } from './components/LetsConnect'
import { Schedule } from './components/Schedule'
import styles from './TrainerBio.module.scss'
import configuration from '../../configuration'

const TrainerBio = ({ ...props }) => {
  const trainerEmailPrefixFromUrl = props.match.params.emailPrefix

  const {
    trainer,
    isLoading: isTrainerLoading,
    error: trainerError,
  } = useAppSelector(trainerSelector)
  const { trainerSchedule } = useAppSelector(trainerScheduleSelector)

  const history = useHistory()
  const dispatch = useAppDispatch()
  const location = useLocation()

  const { isLoggedIn } = useAppSelector(authSelector)

  const [isPageInitialized, setIsPageInitialized] = useState(false)

  const shouldRenderContent =
    isPageInitialized && !isTrainerLoading && !trainerError

  useEffect(() => {
    dispatch(trainerScheduleReset())
    dispatch(getTrainer(trainerEmailPrefixFromUrl))
    setIsPageInitialized(true)
  }, [dispatch, trainerEmailPrefixFromUrl])

  useEffect(() => {
    trainer.nationTrainerId &&
      dispatch(getTrainerSchedule(trainer.nationTrainerId))
  }, [dispatch, trainer.nationTrainerId])

  const deviceWidth = useDeviceWidth()
  const isMobile = deviceWidth < breakpoints.mobile
  const isTabletSmall = deviceWidth < breakpoints.tabletMedium
  const isDesktop = deviceWidth >= breakpoints.desktop

  const headerPhoto =
    trainer.trainerBio?.headerPhoto ||
    configuration.defaultTrainerBioHeaderPhoto

  const hasCoverPhoto = !!headerPhoto
  const hasAboutMeData =
    !!trainer?.trainerBio?.aboutMe ||
    !!trainer?.trainerBio?.trainerAttributes.length
  const hasFitnessBackgroundData =
    !!trainer?.trainerBio?.fitnessBackgrounds.length
  const hasSocialsData = !!trainer?.trainerBio?.socialMedias.length

  const filteredLanguages =
    trainer?.trainerBio?.languages.filter((lang) => lang !== 'ENGLISH') || []

  const redirectToLoginPage = () => {
    dispatch(redirectToPath(location.pathname))
    history.push('/login')
  }

  return shouldRenderContent ? (
    <div className={styles.innerMain}>
      <CoverPhotoPanel headerPhoto={headerPhoto} />
      <div className={classNames(grid.container, styles.content)}>
        <div className={grid.row}>
          <article
            className={classNames(
              grid.col12,
              grid.colMd6,
              grid.colLg4,
              styles.bio,
            )}
          >
            <header className={styles.header}>
              <TrainerAvatar
                imageSrc={trainer.photo}
                isOverlayingCover={hasCoverPhoto}
              />
              <TrainerInfo
                firstName={trainer.firstName}
                lastName={trainer.lastName}
                location={trainerSchedule.trainer_region?.region_name}
                languages={filteredLanguages}
                rating={0}
                ratingsNumber={0}
                trainerSince={trainer.startDate}
                isMobile={isMobile}
              />
            </header>
            <ActionPanel
              isMobile={isMobile}
              isLoggedIn={isLoggedIn}
              phoneNumber={trainer.contactNumber}
              onLoginClick={redirectToLoginPage}
            />
            <div className={styles.infoSectionsWrapper}>
              {hasAboutMeData && (
                <AboutMe
                  isMobile={isMobile}
                  text={trainer.trainerBio?.aboutMe}
                  trainerAttributes={trainer.trainerBio?.trainerAttributes}
                />
              )}
              {hasFitnessBackgroundData && (
                <FitnessBackground
                  isMobile={isMobile}
                  texts={trainer.trainerBio?.fitnessBackgrounds}
                />
              )}
              {hasSocialsData && (
                <LetsConnect
                  isMobile={isMobile}
                  socials={trainer.trainerBio?.socialMedias}
                />
              )}
            </div>
          </article>
          <article
            className={classNames(
              grid.col12,
              grid.colMd6,
              grid.colLg8,
              styles.schedule,
              { [styles.topPadding]: hasCoverPhoto },
            )}
          >
            <Schedule
              workoutSchedule={trainerSchedule}
              isDesktop={isDesktop}
              isTabletSmall={isTabletSmall}
            />
          </article>
        </div>
      </div>
    </div>
  ) : null
}

export default TrainerBio
